import { FC } from 'react';
import { useQueryClient } from 'react-query';
import { DigitalFormsQueries as queryEndpointKeys } from '@frontend/api-digital-forms';
import { useDigitalFormsLocationsContext } from '@frontend/digital-forms-scope';
import { Trans, useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { Chip, Modal, SkeletonLoader, Text } from '@frontend/design-system';
import { pendoTags } from '../../constants';
import { useEnhancedSendFormsFlow } from '../../hooks';
import { useSendFormsStore, useSendFormsContext } from '../../providers';
import { SendFormStep } from '../../types';
import { DigitalFormsSendingExperienceTracker } from '../../utils';
import { marginFixStyle, optionalTextStyle, textStyle } from './header.styles';

const HeaderTitle: FC = () => {
  const { t } = useTranslation('digital-forms-sending-experience');
  const { currentStep, selectedDeliveryMethod } = useSendFormsStore(['currentStep', 'selectedDeliveryMethod']);
  const [isEnhancedFlowEnabled, isLoading] = useEnhancedSendFormsFlow();

  switch (currentStep) {
    case SendFormStep.SELECT_CONTACT:
      return t('Select Recipient');

    case SendFormStep.SELECT_APPOINTMENT:
      if (isLoading) {
        return <SkeletonLoader isText width={300} />;
      }

      if (isEnhancedFlowEnabled) {
        return t('Add More Details');
      }

      return (
        <>
          <Trans t={t}>
            <span css={textStyle}>
              Link Appointment & Attach Documents
              <Text as='span' color='subdued' css={optionalTextStyle}>
                (Optional)
              </Text>
            </span>
          </Trans>
        </>
      );

    case SendFormStep.SELECT_DELIVERY_METHOD:
      return t('Select Delivery Method');

    case SendFormStep.SELECT_DOCUMENT:
      return t('Select Document');

    case SendFormStep.SELECTED_MESSAGE_MODE:
      return selectedDeliveryMethod === 'email' ? t('Share via Email') : t('Share via Kiosk');

    case SendFormStep.SELECT_LOCATION:
      return t('Select Location');

    default:
      return '';
  }
};

const SendFormsHeader: FC = () => {
  const { sendFormsModalControls, origin } = useSendFormsContext();
  const { isGeneratingSolicitedLink, clearStore, locationId, currentStep } = useSendFormsStore([
    'isGeneratingSolicitedLink',
    'clearStore',
    'locationId',
    'currentStep',
  ]);
  const { getScopeName } = useAppScopeStore();
  const { validFormsLocations } = useDigitalFormsLocationsContext();
  const queryClient = useQueryClient();
  const shouldShowLocationBadge = validFormsLocations.length > 1 && currentStep === SendFormStep.SELECT_CONTACT;

  function closeClickHandler() {
    if (isGeneratingSolicitedLink) {
      return;
    }

    DigitalFormsSendingExperienceTracker.finishSendFormTracking({
      isComplete: false,
      origin,
      step: currentStep,
    });
    sendFormsModalControls.closeModal();
    clearStore(origin);
    queryClient.invalidateQueries(queryEndpointKeys.endpointKeys.submissionDetailsForPerson);
  }

  return (
    <Modal.Header onClose={closeClickHandler} closeBtnTrackingId={pendoTags.close}>
      <HeaderTitle />
      {shouldShowLocationBadge && <Chip.Location css={marginFixStyle}>{getScopeName(locationId)}</Chip.Location>}
    </Modal.Header>
  );
};

export default SendFormsHeader;
